import PropTypes from 'prop-types'
import { PopoverImg } from '@vzmi/shp_shared_compo'
import { CDN } from '@vzmi/shp_shared_compo/dist/constants/cdn.js'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import _ from 'lodash'
import { PROPOSAL_TYPE } from '@vzmi/shp_shared_compo/constants/proposal'
import { IconPopoverLabel } from '../../../../Proposal/Common/ProposalForm/FormLabelTooltip/FormLabelTooltip'
import { Field, formDecorator, formValueSelector } from '../../../../Common/forms'
import {
  FormRow,
  InputField,
  RadioGroupInputField,
  RFSelectInputField
} from '../../../../Common/Form'
import CategorySelectorFields from '../../../../Proposal/Common/ProposalForm/CategorySelector/CategorySelectorField'
import {
  fetchRecentlyCategoryOptions,
  fetchCategoryOptions,
  fetchStruDataAttrClusterOptions
} from '../../../../../actions/serviceActions/proposal'
import useUrlFormValues from '../hooks/useUrlFormValues'
import useCategoryAttributes from '../hooks/useCategoryAttributes'
import { required } from '../../../../../lib/FormHelper'
import { transformSubStationOptions } from '../../../../../lib/optionNormalizer'
import styles from './ExternalUrlFormStep.css'
import {
  FORM_NAME,
  FIELD,
  FIELD_LABELS,
  PLATFORM_OPTIONS,
  PRODUCT_ATTRIBUTE_OPTIONS
} from '../constants'

const FORM_CONFIG = {
  form: FORM_NAME,
  enableReinitialize: true
}

// Get placeholder based on platform
const getPlaceholder = (platform) => {
  switch (platform) {
    case 'momo':
      return 'https://www.momoshop.com.tw/goods/GoodsDetail.jsp'
    case 'shopee':
      return 'https://www.shopee.tw/*'
    default:
      return `請先選擇${FIELD_LABELS[FIELD.PLATFORM]}`
  }
}

// Platform-based URL validation
export const validateExternalUrl = (value, allValues) => {
  const platform = _.get(allValues, FIELD.PLATFORM)
  // First check if field is required
  const requiredError = required({ msg: `請輸入${FIELD_LABELS[FIELD.EXTERNAL_URL]}` })(value, allValues)
  if (requiredError) {
    return requiredError
  }

  if (!platform) {
    return undefined
  }

  const trimmedValue = _.trim(value)

  switch (platform) {
    case 'momo': {
      const momoPattern = /^(https:\/\/)?(www\.)?momoshop\.com\.tw\/goods\/GoodsDetail\.jsp([?#].*)?$/i
      if (!momoPattern.test(trimmedValue)) {
        return '請輸入有效的momo商品網址'
      }
      break
    }
    case 'shopee': {
      const shopeePattern = /^(https:\/\/)?(www\.)?shopee\.tw/
      if (!shopeePattern.test(trimmedValue)) {
        return '請輸入有效的shopee商品網址'
      }
      break
    }
    default:
      return undefined
  }

  return undefined
}

const ExternalUrlFormStepForm = ({ subStationOptions = [], handleSubmit, onSubmit, platform, subStationId, actions, change }) => {
  const { setCategoryAttributes } = useCategoryAttributes()
  const triggerFieldChange = (...props) => {
    const [name, value] = props
    if (name === 'setting.struDataAttrCluster' && !_.isEmpty(value)) {
      const attributes = _.get(value, 'attributes', [])
      if (_.isEmpty(attributes)) {
        return change(...props)
      }

      setCategoryAttributes(attributes)
    }
    return change(...props)
  }

  const onRecentlyAttributesChange = (recentlyCategoryData) => {
    const attributes = _.get(recentlyCategoryData, 'categoryPaths[2].attrCluster.attributes', [])
    if (_.isEmpty(attributes)) {
      return
    }

    setCategoryAttributes(attributes)
  }

  return (
    <form
      id={FORM_NAME}
      onSubmit={handleSubmit(onSubmit)}
      className={styles.form}
    >
      <FormRow
        key='platform'
        label={FIELD_LABELS[FIELD.PLATFORM]}
        required
      >
        <Field
          name={FIELD.PLATFORM}
          component={RFSelectInputField}
          options={[
            {
              label: '請選擇',
              value: null
            },
            ...PLATFORM_OPTIONS
          ]}
          validate={required({ msg: `請選擇${FIELD_LABELS[FIELD.PLATFORM]}` })}
          simpleValue
          data-role='platform'
          className={styles.platformSelect}
          showErrorAfterSubmitted
          keepErrorPosition
        />
      </FormRow>
      <FormRow
        key='url'
        label={FIELD_LABELS[FIELD.EXTERNAL_URL]}
        required
      >
        <Field
          name={FIELD.EXTERNAL_URL}
          component={InputField}
          validate={validateExternalUrl}
          placeholder={getPlaceholder(platform)}
          data-role='external-url'
          className={styles.externalUrl}
          showErrorAfterSubmitted
          keepErrorPosition
        />
      </FormRow>
      <FormRow
        key='subStationId'
        label={FIELD_LABELS[FIELD.SUB_STATION_ID]}
        required
      >
        <Field
          name={FIELD.SUB_STATION_ID}
          component={RFSelectInputField}
          validate={required({ msg: `請選擇${FIELD_LABELS[FIELD.SUB_STATION_ID]}` })}
          options={subStationOptions}
          data-role='sub-station-id'
          className={styles.subStationSelect}
          showErrorAfterSubmitted
          keepErrorPosition
          simpleValue
        />
      </FormRow>
      <FormRow
        key='category'
        label={FIELD_LABELS[FIELD.CATEGORY]}
        required
      >
        <CategorySelectorFields
          fetchRecentlyCategoryOptions={actions.fetchRecentlyCategoryOptions}
          fetchCategoryOptions={actions.fetchCategoryOptions}
          fetchStruDataAttrClusterOptions={actions.fetchStruDataAttrClusterOptions}
          triggerFieldChange={triggerFieldChange}
          proposalType={PROPOSAL_TYPE.NEW_LISTING}
          onRecentlyAttributesChange={onRecentlyAttributesChange}
          readOnly={!subStationId}
          mustShowFormError={false}
          hideFullCategoryPath
        />
        <Field
          name={FIELD.CATEGORY}
          component={InputField}
          className={styles.categoryInput}
          validate={required({ msg: `請輸入${FIELD_LABELS[FIELD.CATEGORY]}` })}
          showErrorAfterSubmitted
          keepErrorPosition
        />
      </FormRow>
      <FormRow
        key='productAttribute'
        label={(
          <IconPopoverLabel
            popoverContent={(
              <PopoverImg
                url={`${CDN}/img/tooltip_value.png`}
                width={500}
                height={400}
              />
            )}
          >
            {FIELD_LABELS[FIELD.PRODUCT_ATTRIBUTE]}
          </IconPopoverLabel>
        )}
        labelAlign='top'
        required
      >
        <Field
          name={FIELD.PRODUCT_ATTRIBUTE}
          component={RadioGroupInputField}
          validate={required({ msg: `請選擇${FIELD_LABELS[FIELD.PRODUCT_ATTRIBUTE]}` })}
          options={PRODUCT_ATTRIBUTE_OPTIONS}
          buttonStyle='outline'
          data-role='product-attribute'
          showErrorAfterSubmitted
          keepErrorPosition
        />
      </FormRow>
    </form>
  )
}

ExternalUrlFormStepForm.propTypes = {
  subStationOptions: PropTypes.array,
  handleSubmit: PropTypes.func,
  onSubmit: PropTypes.func,
  platform: PropTypes.string,
  subStationId: PropTypes.any,
  actions: PropTypes.object,
  change: PropTypes.func
}

const ExternalUrlFormStepFormWithDecorator = formDecorator(FORM_CONFIG)(ExternalUrlFormStepForm)

const ExternalUrlFormStepContainer = (props) => {
  const { nextStep } = props
  const [, setUrlFormValues] = useUrlFormValues()
  const { subStationOptions = [], platform, subStationId, actions, initialValues } = props

  const handleFormSubmit = (formValues) => {
    // Form validation is handled by redux-form
    // On successful validation, formValues will be passed here
    setUrlFormValues(formValues)
    nextStep()
  }

  return (
    <ExternalUrlFormStepFormWithDecorator
      {...props}
      subStationOptions={subStationOptions}
      onSubmit={handleFormSubmit}
      platform={platform}
      subStationId={subStationId}
      actions={actions}
      initialValues={initialValues}
    />
  )
}

ExternalUrlFormStepContainer.propTypes = {
  nextStep: PropTypes.func.isRequired
}

const selector = formValueSelector(FORM_CONFIG.form)

const mapStateToProps = (state) => {
  const subStations = _.get(state, 'proposal.subStations', [])
  const subStationOptions = transformSubStationOptions(subStations, state.flags)
  const subStationOptionsWithDefault = [
    {
      label: '請選擇',
      value: null
    },
    ...subStationOptions
  ]
  const defaultSubStationId = _.get(subStationOptions, '0.value', null)

  // Compute initialValues object for Redux Form
  const initialValues = {
    [FIELD.PLATFORM]: undefined,
    [FIELD.EXTERNAL_URL]: '',
    [FIELD.SUB_STATION_ID]: defaultSubStationId,
    [FIELD.PRODUCT_ATTRIBUTE]: PRODUCT_ATTRIBUTE_OPTIONS[0].value
  }
  _.set(initialValues, FIELD.CATEGORY)

  return {
    subStationOptions: subStationOptionsWithDefault,
    initialValues,
    platform: selector(state, FIELD.PLATFORM),
    subStationId: selector(state, FIELD.SUB_STATION_ID)
  }
}

const mapDispatchToProps = (dispatch) => {
  const actions = bindActionCreators(
    {
      fetchRecentlyCategoryOptions,
      fetchCategoryOptions,
      fetchStruDataAttrClusterOptions
    },
    dispatch
  )

  return {
    actions
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ExternalUrlFormStepContainer)
